import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Drawer, CodeToken, CopyButton } from '../components/ui';
import './Behaviour.css';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface BehaviourProps {
  apiUrl: string;
}

interface TopSignal {
  type: string;
  label: string;
}

interface OverviewAgent {
  agent_id: string;
  name: string;
  role: string;
  status: string;
  drift_score: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  top_signals: TopSignal[];
  recent_calls: number;
  sample_count: number;
  has_baseline: boolean;
  insufficient_history: boolean;
  baseline_age_hours: number | null;
}

interface OverviewUser {
  agent_id: string;
  agent_name: string;
  end_user: string;
  drift_score: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  top_signals: TopSignal[];
  recent_calls: number;
  sample_count: number;
  has_baseline: boolean;
  insufficient_history: boolean;
}

interface BehaviourStatus {
  engine: 'llm' | 'template';
  model: string;
  ollamaHealthy: boolean;
  baseline_count: number;
  last_baseline_run: string | null;
}

interface Signal {
  type: string;
  label: string;
  detail: string;
  points: number;
}

interface AgentDetail {
  agent_id: string;
  end_user?: string | null;
  drift_score: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  signals: Signal[];
  recent: {
    total_calls: number;
    tools: string[];
    services: string[];
    hourly: number[];
    peak_hour_calls: number;
    error_rate: number;
    deny_rate: number;
  };
  has_baseline: boolean;
  insufficient_history: boolean;
  sample_count: number;
  baseline_age_hours: number | null;
  window_hours: number;
  narrative: string;
  recommendation: string;
  narrative_source: 'llm' | 'template';
  baseline: {
    sample_count: number;
    window_start: string | null;
    computed_at: string | null;
    profile: {
      known_tools: string[];
      known_services: string[];
      hourly: number[];
      rate_per_hour: { mean: number; stddev: number };
      error_rate: number;
      deny_rate: number;
      total_calls: number;
    };
  } | null;
}

const severityColor = (s: string): string =>
  s === 'critical' || s === 'high' ? 'var(--danger)' : s === 'medium' ? 'var(--warning)' : 'var(--info)';

const Behaviour: React.FC<BehaviourProps> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const [agents, setAgents] = useState<OverviewAgent[]>([]);
  const [people, setPeople] = useState<OverviewUser[]>([]);
  const [status, setStatus] = useState<BehaviourStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<AgentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/admin/behaviour/overview`);
      if (res.ok) {
        const data = await res.json();
        setAgents(data.agents || []);
        setPeople(data.users || []);
        setStatus(data.status || null);
      }
    } catch (e) {
      console.error('Failed to fetch behaviour overview:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const subjectQuery = (user?: string | null) => (user ? `?user=${encodeURIComponent(user)}` : '');

  const openAgent = async (agentId: string, user?: string | null) => {
    setDetailLoading(true);
    setDetail({ agent_id: agentId, end_user: user || null } as AgentDetail);
    try {
      const res = await fetch(`${apiUrl}/admin/behaviour/agent/${encodeURIComponent(agentId)}${subjectQuery(user)}`);
      if (res.ok) setDetail(await res.json());
    } catch (e) {
      console.error('Failed to fetch agent behaviour:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const rebuildBaseline = async (agentId: string, user?: string | null) => {
    setRebuilding(true);
    try {
      await fetch(`${apiUrl}/admin/behaviour/agent/${encodeURIComponent(agentId)}/rebuild${subjectQuery(user)}`, { method: 'POST' });
      await openAgent(agentId, user);
      await fetchOverview();
    } catch (e) {
      console.error('Failed to rebuild baseline:', e);
    } finally {
      setRebuilding(false);
    }
  };

  const fmtAge = (h: number | null): string => {
    if (h === null) return 'never';
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="signal" size={20} /> {tr('pages.behaviour.agentBehaviour')}
          </h1>
          <p className="subtitle">
            {tr('pages.behaviour.eachAgentIsScoredAgainstA')}
          </p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          {status && (
            <span className={`bh-engine ${status.engine === 'llm' ? 'live' : ''}`} title={status.model}>
              <Icon name={status.engine === 'llm' ? 'bolt' : 'document'} />
              {status.engine === 'llm' ? `local LLM (${status.model})` : tr('pages.behaviour.ruleBasedNarration')}
            </span>
          )}
          <button className="ent-btn" onClick={fetchOverview} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} />
            {tr('pages.behaviour.refresh')}
          </button>
        </div>
      </div>

      {status && (
        <div className="bh-summary">
          <div className="bh-stat">
            <span className="bh-stat-val">{agents.length}</span>
            <span className="bh-stat-label">{tr('pages.behaviour.agentsTracked')}</span>
          </div>
          <div className="bh-stat">
            <span className="bh-stat-val">{agents.filter((a) => a.drift_score >= 30).length}</span>
            <span className="bh-stat-label">{tr('pages.behaviour.drifting30')}</span>
          </div>
          <div className="bh-stat">
            <span className="bh-stat-val">{status.baseline_count}</span>
            <span className="bh-stat-label">{tr('pages.behaviour.baselinesLearned')}</span>
          </div>
          <div className="bh-stat">
            <span className="bh-stat-val">{status.last_baseline_run ? parseServerDate(status.last_baseline_run).toLocaleString() : '—'}</span>
            <span className="bh-stat-label">{tr('pages.behaviour.lastBaselineRun')}</span>
          </div>
        </div>
      )}

      {loading && agents.length === 0 ? (
        <p className="muted-text" style={{ textAlign: 'center', padding: 32 }}>{tr('pages.behaviour.analyzingAgentBehaviour')}</p>
      ) : agents.length === 0 ? (
        <p className="muted-text" style={{ textAlign: 'center', padding: 32 }}>{tr('pages.behaviour.noAgentsToAnalyzeYet')}</p>
      ) : (
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th>{tr('pages.behaviour.agent')}</th>
                <th style={{ width: 180 }}>{tr('pages.behaviour.drift')}</th>
                <th>{tr('pages.behaviour.signals')}</th>
                <th style={{ width: 90 }}>{tr('pages.behaviour.24hCalls')}</th>
                <th style={{ width: 130 }}>{tr('pages.behaviour.baseline')}</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.agent_id} className="bh-row" onClick={() => openAgent(a.agent_id)}>
                  <td>
                    <div className="bh-agent">
                      <span className="bh-agent-name">{a.name}</span>
                      <span className="bh-agent-role">{a.role}</span>
                    </div>
                  </td>
                  <td>
                    {a.insufficient_history ? (
                      <span className="muted-text" style={{ fontSize: 12 }}>{tr('pages.behaviour.insufficientHistory')}</span>
                    ) : (
                      <div className="bh-score">
                        <div className="bh-score-track">
                          <div
                            className="bh-score-fill"
                            style={{ width: `${a.drift_score}%`, background: severityColor(a.severity) }}
                          />
                        </div>
                        <span className="bh-score-num" style={{ color: severityColor(a.severity) }}>
                          {a.drift_score}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="bh-chips">
                      {a.top_signals.length ? (
                        a.top_signals.map((s, i) => (
                          <span key={i} className={`bh-chip bh-chip-${s.type}`}>{s.label}</span>
                        ))
                      ) : (
                        <span className="muted-text" style={{ fontSize: 12 }}>
                          {a.insufficient_history ? '—' : tr('pages.behaviour.withinBaseline')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="mono">{a.recent_calls}</td>
                  <td className="muted-text" style={{ fontSize: 12 }}>
                    {a.has_baseline ? `${a.sample_count} calls · ${fmtAge(a.baseline_age_hours)}` : tr('pages.behaviour.none')}
                  </td>
                  <td>
                    <button className="ent-btn sm ghost" onClick={(e) => { e.stopPropagation(); openAgent(a.agent_id); }}>
                      <Icon name="view" /> {tr('pages.behaviour.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {people.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{tr('pages.behaviour.peopleTitle')}</h3>
          <p className="muted-text" style={{ margin: '0 0 10px', fontSize: 12 }}>{tr('pages.behaviour.peopleSubtitle')}</p>
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{tr('pages.behaviour.user')}</th>
                  <th style={{ width: 180 }}>{tr('pages.behaviour.drift')}</th>
                  <th>{tr('pages.behaviour.signals')}</th>
                  <th style={{ width: 90 }}>{tr('pages.behaviour.24hCalls')}</th>
                  <th style={{ width: 130 }}>{tr('pages.behaviour.baseline')}</th>
                </tr>
              </thead>
              <tbody>
                {people.map((u) => (
                  <tr key={`${u.agent_id}|${u.end_user}`} className="bh-row" onClick={() => openAgent(u.agent_id, u.end_user)}>
                    <td>
                      <div className="bh-agent">
                        <span className="bh-agent-name">{u.end_user}</span>
                        <span className="bh-agent-role">{tr('pages.behaviour.via', { agent: u.agent_name })}</span>
                      </div>
                    </td>
                    <td>
                      {u.insufficient_history ? (
                        <span className="muted-text" style={{ fontSize: 12 }}>{tr('pages.behaviour.insufficientHistory')}</span>
                      ) : (
                        <div className="bh-score">
                          <div className="bh-score-track">
                            <div className="bh-score-fill" style={{ width: `${u.drift_score}%`, background: severityColor(u.severity) }} />
                          </div>
                          <span className="bh-score-num" style={{ color: severityColor(u.severity) }}>{u.drift_score}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="bh-chips">
                        {u.top_signals.length ? (
                          u.top_signals.map((s, i) => <span key={i} className={`bh-chip bh-chip-${s.type}`}>{s.label}</span>)
                        ) : (
                          <span className="muted-text" style={{ fontSize: 12 }}>{u.insufficient_history ? '—' : tr('pages.behaviour.withinBaseline')}</span>
                        )}
                      </div>
                    </td>
                    <td className="mono">{u.recent_calls}</td>
                    <td className="muted-text" style={{ fontSize: 12 }}>
                      {u.has_baseline ? `${u.sample_count} calls` : tr('pages.behaviour.none')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <Drawer title={tr('pages.behaviour.agentBehaviourDetail')} onClose={() => setDetail(null)} width={560}>
          {detailLoading || !detail.recent ? (
            <p className="muted-text" style={{ padding: 24 }}>{tr('pages.behaviour.analyzing')}</p>
          ) : (
            <div className="bh-detail">
              <div className="bh-detail-head">
                <div>
                  <div className="bh-detail-id mono">
                    {detail.end_user || detail.agent_id} <CopyButton value={detail.end_user || detail.agent_id} />
                  </div>
                  {detail.end_user && <div className="muted-text mono" style={{ fontSize: 11 }}>{detail.agent_id}</div>}
                  <span className={`bh-source ${detail.narrative_source === 'llm' ? 'live' : ''}`}>
                    {detail.narrative_source === 'llm' ? tr('pages.behaviour.explainedByLocalLLM') : tr('pages.behaviour.ruleBasedExplanation')}
                  </span>
                </div>
                {!detail.insufficient_history && (
                  <div className="bh-detail-score" style={{ borderColor: severityColor(detail.severity), color: severityColor(detail.severity) }}>
                    <span className="bh-detail-score-num">{detail.drift_score}</span>
                    <span className="bh-detail-score-sev">{detail.severity}</span>
                  </div>
                )}
              </div>

              <div className="bh-narrative" style={{ borderColor: severityColor(detail.severity) }}>
                <p>{detail.narrative}</p>
                <p className="bh-reco"><Icon name="checkCircle" /> {detail.recommendation}</p>
              </div>

              {detail.insufficient_history ? (
                <div className="bh-empty-note">
                  <Icon name="info" /> {tr('pages.behaviour.aBaselineNeedsMoreHistoryBefore')}
                </div>
              ) : (
                <>
                  {detail.signals.length > 0 && (
                    <div className="bh-section">
                      <h4>{tr('pages.behaviour.whatChanged')}</h4>
                      {detail.signals.map((s, i) => (
                        <div key={i} className="bh-signal">
                          <span className={`bh-chip bh-chip-${s.type}`}>{s.label}</span>
                          <span className="bh-signal-detail">{s.detail}</span>
                          <span className="bh-signal-pts">+{s.points}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {detail.baseline && (
                    <div className="bh-section">
                      <h4>{tr('pages.behaviour.activeHoursBaselineVsLast24h')}</h4>
                      <div className="bh-heat-row">
                        <span className="bh-heat-label">{tr('pages.behaviour.base')}</span>
                        <HeatStrip values={detail.baseline.profile.hourly} accent="var(--text-3)" />
                      </div>
                      <div className="bh-heat-row">
                        <span className="bh-heat-label">{tr('pages.behaviour.now')}</span>
                        <HeatStrip values={detail.recent.hourly} accent="var(--accent)" />
                      </div>
                      <div className="bh-heat-axis"><span>{tr('pages.behaviour.0h')}</span><span>{tr('pages.behaviour.6h')}</span><span>{tr('pages.behaviour.12h')}</span><span>{tr('pages.behaviour.18h')}</span><span>{tr('pages.behaviour.23h')}</span></div>
                    </div>
                  )}

                  <div className="bh-section">
                    <h4>{tr('pages.behaviour.toolsUsedInLast24h')}</h4>
                    <div className="bh-tools">
                      {detail.recent.tools.length === 0 && <span className="muted-text" style={{ fontSize: 12 }}>{tr('pages.behaviour.noActivityInTheWindow')}</span>}
                      {detail.recent.tools.map((t) => {
                        const known = detail.baseline?.profile.known_tools.includes(t);
                        return known ? (
                          <CodeToken key={t} muted>{t}</CodeToken>
                        ) : (
                          <span key={t} className="bh-newtool"><Icon name="warning" /> {t}</span>
                        );
                      })}
                    </div>
                  </div>

                  {detail.baseline && (
                    <div className="bh-section bh-meta">
                      <div><span>{tr('pages.behaviour.baselineSamples')}</span><strong>{tr('pages.behaviour.calls', { v0: detail.baseline.sample_count })}</strong></div>
                      <div><span>{tr('pages.behaviour.typicalRate')}</span><strong>{tr('pages.behaviour.activeHr', { v0: detail.baseline.profile.rate_per_hour.mean })}</strong></div>
                      <div><span>{tr('pages.behaviour.baselineErrorRate')}</span><strong>{Math.round(detail.baseline.profile.error_rate * 100)}%</strong></div>
                      <div><span>{tr('pages.behaviour.computed')}</span><strong>{detail.baseline.computed_at ? parseServerDate(detail.baseline.computed_at).toLocaleString() : '—'}</strong></div>
                    </div>
                  )}
                </>
              )}

              <button className="ent-btn primary bh-rebuild" onClick={() => rebuildBaseline(detail.agent_id, detail.end_user)} disabled={rebuilding}>
                <Icon name="refresh" className={rebuilding ? 'spin' : ''} />
                {rebuilding ? tr('pages.behaviour.rebuilding') : tr('pages.behaviour.rebuildBaselineNow')}
              </button>
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
};

const HeatStrip: React.FC<{ values: number[]; accent: string }> = ({ values, accent }) => {
  const { t } = useTranslation();
  const max = Math.max(...values, 1);
  return (
    <div className="bh-heat">
      {values.map((v, i) => (
        <div
          key={i}
          className="bh-heat-cell"
          title={t('pages.behaviour.heatBucket', { hour: i, count: v })}
          style={{ background: v > 0 ? accent : 'var(--border)', opacity: v > 0 ? Math.max(0.2, v / max) : 0.35 }}
        />
      ))}
    </div>
  );
};

export default Behaviour;
